import { useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import { FormInput } from "../../../components/Input";
import { notify } from "../../../components/Notification";
import { DiscardPopUp } from "../../../components/PopUp";
import { VisuallyHidden } from "../../../components/VisuallyHidden";
import { buildUnsavedChangesDetails } from "../../../components/PopUp/confirmShared";
import { useSaveHealthTargetsMutation } from "../../../services";
import { SaveBar } from "../components/SaveBar";
import { HEALTH_TARGET_FIELDS, toPayload } from "./healthTargetsConfig";
import {
  Comparator,
  Hint,
  TargetInputCell,
  TargetLabel,
  TargetRow,
  TargetsCard,
  TargetsForm,
  UnitSuffix,
} from "./styles";
import { HealthTargetFormValues, HealthTargetsFormProps } from "./types";

export const HealthTargetsForm = ({
  values,
  focusMetric,
}: HealthTargetsFormProps) => {
  const [showDiscard, setShowDiscard] = useState(false);
  const [saveTargets] = useSaveHealthTargetsMutation();

  const { control, reset, handleSubmit } = useForm<HealthTargetFormValues>({
    values,
    resetOptions: { keepDirtyValues: true },
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { isDirty, dirtyFields, isSubmitting } = useFormState({ control });
  const discardDetails = buildUnsavedChangesDetails(
    Object.keys(dirtyFields).length,
  );

  const onSubmit = async (data: HealthTargetFormValues) => {
    try {
      await saveTargets(toPayload(data)).unwrap();
      notify.success("Health targets saved!");
      reset(data);
    } catch {
      // errorToastMiddleware surfaces the API error; keep the form dirty.
    }
  };

  const confirmDiscard = () => {
    reset(values, { keepDirtyValues: false });
    setShowDiscard(false);
  };

  return (
    <TargetsForm onSubmit={handleSubmit(onSubmit)} noValidate>
      <Hint>
        The levels your dashboard metrics are measured against. Leave a field
        empty to switch its target off.
      </Hint>

      <TargetsCard>
        {HEALTH_TARGET_FIELDS.map((field) => {
          const inputId = `health-target-${field.metric}`;
          return (
            <TargetRow key={field.metric}>
              <TargetLabel htmlFor={inputId}>
                {field.label}
                <Comparator>{field.comparator}</Comparator>
                <VisuallyHidden as="span">({field.unit})</VisuallyHidden>
              </TargetLabel>
              <TargetInputCell>
                <FormInput
                  id={inputId}
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  inputMode={field.inputMode}
                  disabled={isSubmitting}
                  autoFocus={focusMetric === field.metric}
                />
                <UnitSuffix aria-hidden="true">{field.unit}</UnitSuffix>
              </TargetInputCell>
            </TargetRow>
          );
        })}
      </TargetsCard>

      <SaveBar
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        onSave={handleSubmit(onSubmit)}
        onDiscard={() => setShowDiscard(true)}
      />

      <DiscardPopUp
        isOpen={showDiscard}
        setIsOpen={setShowDiscard}
        eyebrow="EDITING HEALTH TARGETS"
        title="Discard your changes?"
        text="Your edits to your health targets haven't been saved yet. If you leave now they'll be lost — this can't be undone."
        details={discardDetails}
        handleOnDiscard={confirmDiscard}
      />
    </TargetsForm>
  );
};
